/**
 * SuggestionCombobox — the value input of a Company / Past company / Location
 * filter row (PR #962 follow-up F10). It replaces a native `<datalist>`,
 * whose popup the OS draws: it ignored the app theme (a light box in dark
 * mode), could not highlight the typed text or show a count, kept offering
 * values already on another row, and looked different in every browser.
 *
 * Same combobox pattern as `new-import/steps/SearchTypeahead.tsx`, with the
 * same tokens: focus stays on the `role="combobox"` input, the floating
 * `role="listbox"` holds `role="option"` buttons, and the active one is
 * announced through `aria-activedescendant`. `SearchableSelect` (packages/ui)
 * only picks from a list, and a filter value is free text, so it does not fit.
 *
 * Free text stays first-class, but typing is a DRAFT, not a filter value
 * (browser QA 2026-09-24: typing "ac" had previewed `company is "ac"` →
 * "Included (0)", the funnel recomputing on every keystroke). The draft only
 * reaches `onChange` when the recruiter commits it: an option picked (click,
 * or Enter on the active one), Enter with free text (what the empty state's
 * "press Enter to use it anyway" promises), or leaving the field. Leaving
 * commits whatever the box shows, cleared text included, so the text on the
 * screen and the filter applied never disagree once focus has moved on —
 * the same rule as `CommitTextInput`, the Title / Past title / Keyword rows.
 * Esc throws the draft away (back to the committed value) and closes.
 *
 * The listbox is PORTALLED into `<body>` at `position: fixed` (the
 * `SearchTeamCell` / `Tooltip escapesOverflow` pattern), measured from the
 * input and re-measured on scroll / resize while open. Rendered in place it
 * shared a stacking context with the page, and the "Review contacts" card's
 * sticky table header (z-20 / z-30) painted over its options.
 *
 * The suggestions come from `CombinePreviewResponse.filter_suggestions`
 * (every eligible row of the selected lists, most frequent first); a value
 * longer than the per-value cap is not offered, because it could not be sent.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** One suggested value; `count` is how many entries in the selected lists
 *  carry it, before duplicates merge (absent where a count would mislead,
 *  e.g. Past company). */
export interface ComboboxSuggestion {
  value: string;
  count?: number;
}

export interface SuggestionComboboxProps {
  /** The COMMITTED value. */
  value: string;
  /** Called on commit only (pick, Enter, leaving the field), never per keystroke. */
  onChange: (value: string) => void;
  suggestions: readonly ComboboxSuggestion[];
  /** Case-folded, trimmed values already on another row of this field —
   *  not offered again. */
  exclude: ReadonlySet<string>;
  /** "company" / "location" — the empty state's noun. */
  noun: string;
  /** The input's accessible name. */
  label: string;
  placeholder: string;
  /** Per-value character cap (`CombineFilterClause.values` item). */
  maxLength: number;
  testId: string;
}

/** Wrap each case-insensitive occurrence of `query` in a `<mark>` (the same
 *  highlight `SearchTypeahead` and `CompanyTypeahead` draw). */
function highlightMatch(text: string, query: string): React.ReactNode {
  const trimmed = query.trim();
  if (!trimmed) return text;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.toLowerCase() === trimmed.toLowerCase() ? (
      <mark key={`${i}-${part}`} className="rounded-sm bg-mark text-mark-ink">
        {part}
      </mark>
    ) : (
      <span key={`${i}-${part}`}>{part}</span>
    )
  );
}

export function SuggestionCombobox({
  value,
  onChange,
  suggestions,
  exclude,
  noun,
  label,
  placeholder,
  maxLength,
  testId,
}: SuggestionComboboxProps) {
  const listboxId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [expanded, setExpanded] = useState(false);
  // What the box shows. Equal to `value` except while the recruiter is typing.
  const [draft, setDraft] = useState(value);
  // -1: nothing active, the resting state after every change of the text,
  // so Enter never confirms a row nobody moved to (SearchTypeahead's rule).
  const [activeIndex, setActiveIndex] = useState(-1);
  // Viewport coordinates of the portalled listbox; `null` until measured.
  const [placement, setPlacement] = useState<{
    top: number;
    left: number;
    minWidth: number;
  } | null>(null);

  // A committed value changed from outside (another row removed, "Start
  // over") replaces the draft.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const typed = draft.trim();
  const needle = typed.toLocaleLowerCase();
  const options = suggestions.filter(
    (s) =>
      s.value.length <= maxLength &&
      !exclude.has(s.value.trim().toLocaleLowerCase()) &&
      s.value.toLocaleLowerCase().includes(needle)
  );
  const showCounts = options.some((s) => s.count != null);
  // Nothing to show and nothing typed: stay closed rather than open an empty box.
  const open = expanded && (options.length > 0 || typed.length > 0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the text is the trigger; the body only resets
  useEffect(() => {
    setActiveIndex(-1);
  }, [needle]);

  useEffect(() => {
    if (activeIndex >= 0) optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  const measure = useCallback(() => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Sized to its content (`w-max`, capped by `max-w-[30rem]`), but never
    // narrower than the input when the input is narrow, nor than 240px.
    setPlacement({ top: rect.bottom + 4, left: rect.left, minWidth: Math.min(rect.width, 240) });
  }, []);

  // Measured before paint, so the list never shows at its static position.
  useLayoutEffect(() => {
    if (open) measure();
    else setPlacement(null);
  }, [open, measure]);

  // `position: fixed` is viewport-relative: follow the input while open.
  // `capture` because scroll does not bubble and the page's scrollers are
  // inner elements (same reasoning as `Tooltip`).
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", measure, { capture: true, passive: true });
    window.addEventListener("resize", measure, { passive: true });
    return () => {
      window.removeEventListener("scroll", measure, { capture: true });
      window.removeEventListener("resize", measure);
    };
  }, [open, measure]);

  // Close on a press anywhere outside this field. The listbox is portalled,
  // so it is not inside the wrapper and is checked on its own; its options
  // keep focus in the input, so a press on them is not a blur.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !listboxRef.current?.contains(target)) {
        setExpanded(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  /** The only way a value reaches the filter. */
  function commit(next: string) {
    setDraft(next);
    setExpanded(false);
    if (next !== value) onChange(next);
  }

  function moveActive(delta: number) {
    const count = options.length;
    if (count === 0) return;
    setActiveIndex((prev) => {
      if (prev < 0) return delta > 0 ? 0 : count - 1;
      return (prev + delta + count) % count;
    });
  }

  const active = activeIndex >= 0 ? options[activeIndex] : undefined;

  return (
    <div ref={wrapRef} className="relative min-w-32 flex-1">
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setExpanded(true);
        }}
        onFocus={() => setExpanded(true)}
        onBlur={(e) => {
          // Tab (or any focus move) out of the field commits the text and
          // closes the list.
          const to = e.relatedTarget as Node | null;
          if (!wrapRef.current?.contains(to) && !listboxRef.current?.contains(to)) commit(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            // Drops the uncommitted text and closes; a no-op (and not
            // swallowed) when there is nothing to drop or close.
            if (open || draft !== value) {
              e.preventDefault();
              setDraft(value);
              setExpanded(false);
            }
            return;
          }
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!open) {
              setExpanded(true);
              return;
            }
            moveActive(e.key === "ArrowDown" ? 1 : -1);
            return;
          }
          if (e.key === "Enter") {
            // An active option is the value; otherwise the typed text is.
            e.preventDefault();
            commit(open && active ? active.value : draft);
          }
        }}
        placeholder={placeholder}
        aria-label={label}
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={active ? `${listboxId}-opt-${activeIndex}` : undefined}
        maxLength={maxLength}
        autoComplete="off"
        data-testid={testId}
        className="w-full rounded-md border border-border bg-bg px-2 py-1.5 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
      />
      {open
        ? createPortal(
            <div
              ref={listboxRef}
              id={listboxId}
              // biome-ignore lint/a11y/useSemanticElements: a <select> cannot hold the highlight and the count, and would take focus from the input
              role="listbox"
              // Focus stays on the input (combobox pattern); `-1` keeps the list
              // programmatically focusable without adding a tab stop.
              tabIndex={-1}
              aria-label={`${label} suggestions`}
              data-testid={`${testId}-listbox`}
              // Measured viewport pixels only — every static style is a class.
              // Hidden for the frame before the first measurement.
              style={
                placement
                  ? {
                      top: `${placement.top}px`,
                      left: `${placement.left}px`,
                      minWidth: `${placement.minWidth}px`,
                    }
                  : { visibility: "hidden" }
              }
              // z-50: the portalled-layer tier (`Tooltip`, `SearchTeamCell`),
              // above the page's sticky table layers (z-20 / z-30).
              className="fixed z-50 flex max-h-56 w-max max-w-[30rem] flex-col gap-1 overflow-y-auto rounded-md border border-border bg-surface p-1 shadow-lg"
            >
              {options.length === 0 ? (
                <p
                  data-testid={`${testId}-empty`}
                  className="px-1 py-2 text-caption text-text-muted"
                >
                  No {noun} in these lists matches “{typed}” — press Enter to use it anyway
                </p>
              ) : (
                options.map((opt, i) => (
                  <button
                    key={opt.value}
                    // biome-ignore lint/a11y/useSemanticElements: <option> renders text only and cannot live outside a <select>
                    role="option"
                    aria-selected={i === activeIndex}
                    id={`${listboxId}-opt-${i}`}
                    ref={(el) => {
                      optionRefs.current[i] = el;
                    }}
                    type="button"
                    tabIndex={-1}
                    data-testid={`${testId}-option-${i}`}
                    // Keeps focus in the input, so the press is inside the field
                    // and the blur handler never closes the list under the click.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => commit(opt.value)}
                    // Pointer and keyboard share one highlight.
                    onMouseEnter={() => setActiveIndex(i)}
                    title={opt.value}
                    className={`flex min-w-0 items-baseline gap-1.5 rounded-md px-2 py-1.5 text-left text-body text-text ${
                      i === activeIndex ? "bg-accent-muted" : ""
                    }`}
                  >
                    <span className="min-w-0 truncate">{highlightMatch(opt.value, typed)}</span>
                    {opt.count != null ? (
                      <span className="shrink-0 text-caption text-text-secondary tabular-nums">
                        {" "}
                        · {opt.count.toLocaleString()}
                      </span>
                    ) : null}
                  </button>
                ))
              )}
              {showCounts ? (
                <p
                  data-testid={`${testId}-counts-note`}
                  className="px-1 pt-1 text-caption text-text-secondary"
                >
                  Numbers count entries in the selected lists: someone in two lists counts twice.
                </p>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
